import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { ImageUpload } from "./ImageUpload";
import { GalleryUpload } from "./GalleryUpload";
import { Field, Toggle, numOrNull } from "./Field";

const STATUSES = ["Coming Soon", "On Sale", "Limited Availability", "Sold Out"];
export const EMPTY_CONCERT = {
  artist: "", image: "", description: "", supporting_acts: [], date: "", doors_time: "19:00", show_time: "20:00",
  genre: "", venue: "", address: "", directions: "", price_from: null, status: "Coming Soon", ticket_url: "",
  ticket_options: [], gallery: [], is_sample: false,
};

export const ConcertForm = ({ initial, onSubmit, busy }) => {
  const [f, setF] = useState({ ...EMPTY_CONCERT, ...initial });
  const [acts, setActs] = useState((initial?.supporting_acts || []).join(", "));
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setOpt = (i, k, v) => setF({ ...f, ticket_options: f.ticket_options.map((o, j) => (j === i ? { ...o, [k]: v } : o)) });

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...f,
      price_from: numOrNull(f.price_from),
      supporting_acts: acts.split(",").map((s) => s.trim()).filter(Boolean),
      ticket_options: f.ticket_options.filter((o) => o.name).map((o) => ({ ...o, price: numOrNull(o.price) })),
    });
  };

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="concert-form">
      <Field label="Artist *"><input required data-testid="cf-artist" className="admin-input" value={f.artist} onChange={set("artist")} /></Field>
      <Field label="Genre *"><input required data-testid="cf-genre" className="admin-input" value={f.genre} onChange={set("genre")} placeholder="e.g. Rock" /></Field>
      <div className="sm:col-span-2"><ImageUpload value={f.image} onChange={set("image")} testId="cf-image" label="Artist image" /></div>
      <Field label="Date *"><input required type="date" data-testid="cf-date" className="admin-input" value={f.date} onChange={set("date")} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Doors *"><input required type="time" data-testid="cf-doors" className="admin-input" value={f.doors_time} onChange={set("doors_time")} /></Field>
        <Field label="Show *"><input required type="time" data-testid="cf-show" className="admin-input" value={f.show_time} onChange={set("show_time")} /></Field>
      </div>
      <Field label="Ticket status">
        <select data-testid="cf-status" className="admin-input" value={f.status} onChange={set("status")}>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Starting price (CAD) — leave blank if not confirmed"><input type="number" min="0" step="1" data-testid="cf-price" className="admin-input" value={f.price_from ?? ""} onChange={set("price_from")} /></Field>
      <Field label="Ticket link (external URL)" className="sm:col-span-2"><input type="url" data-testid="cf-ticket-url" className="admin-input" value={f.ticket_url} onChange={set("ticket_url")} placeholder="https://" /></Field>
      <Field label="Supporting acts (comma separated)" className="sm:col-span-2"><input data-testid="cf-acts" className="admin-input" value={acts} onChange={(e) => setActs(e.target.value)} /></Field>
      <Field label="Description" className="sm:col-span-2"><textarea rows={3} data-testid="cf-description" className="admin-input" value={f.description} onChange={set("description")} /></Field>
      <Field label="Venue name *"><input required data-testid="cf-venue" className="admin-input" value={f.venue} onChange={set("venue")} /></Field>
      <Field label="Address"><input data-testid="cf-address" className="admin-input" value={f.address} onChange={set("address")} /></Field>
      <Field label="Directions" className="sm:col-span-2"><textarea rows={2} data-testid="cf-directions" className="admin-input" value={f.directions} onChange={set("directions")} /></Field>

      <div className="space-y-2 sm:col-span-2">
        <p className="text-sm text-zinc-300">Ticket options</p>
        {f.ticket_options.map((o, i) => (
          <div key={i} className="grid grid-cols-[1fr_90px_1fr_auto] gap-2" data-testid={`cf-option-${i}`}>
            <input className="admin-input" placeholder="Name" value={o.name} onChange={(e) => setOpt(i, "name", e.target.value)} data-testid={`cf-option-name-${i}`} />
            <input className="admin-input" type="number" min="0" placeholder="CA$" value={o.price ?? ""} onChange={(e) => setOpt(i, "price", e.target.value)} data-testid={`cf-option-price-${i}`} />
            <input className="admin-input" placeholder="Note" value={o.note} onChange={(e) => setOpt(i, "note", e.target.value)} />
            <button type="button" aria-label="Remove option" onClick={() => setF({ ...f, ticket_options: f.ticket_options.filter((_, j) => j !== i) })} className="px-2 text-zinc-400 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
        <button type="button" data-testid="cf-add-option" onClick={() => setF({ ...f, ticket_options: [...f.ticket_options, { name: "", price: "", note: "" }] })} className="btn-ghost"><Plus size={15} /> Add option</button>
      </div>
      <div className="sm:col-span-2"><GalleryUpload value={f.gallery || []} onChange={set("gallery")} /></div>
      <div className="sm:col-span-2"><Toggle label="Mark as sample (not final)" checked={f.is_sample} onChange={set("is_sample")} testId="cf-sample" /></div>
      <button data-testid="cf-submit" disabled={busy} className="btn-primary justify-center sm:col-span-2">{busy ? "Saving…" : "Save Concert"}</button>
    </form>
  );
};
